import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import {
  getPublicUrl,
  supabase,
} from '../../core/supabase/supabase.client';
import { ProductImage } from '../../models/product-image.model';
import { ProductVariant } from '../../models/product-variant.model';
import { ImageAssetUploadService } from '../utils/image-asset-upload.service';

/* ===================== TYPES ===================== */

export interface ProductMutationPayload {
  name?: string;
  description?: string | null;
  search_keywords?: string | null;
  is_active?: boolean;
  brand_id?: number | null;
  food_type?: 'veg' | 'non_veg' | 'egg' | 'not_applicable' | null;
  highlights?: ProductHighlight[] | null;
  ingredients?: string | null;
  allergen_info?: string | null;
  usage_instructions?: string | null;
  storage_instructions?: string | null;
  country_of_origin?: string | null;
  manufacturer_details?: string | null;
  marketer_details?: string | null;
  importer_details?: string | null;
  fssai_license?: string | null;
  disclaimer?: string | null;
  attributes?: Record<string, any> | null;
}

export interface ProductHighlight {
  label: string;
  value: string;
  is_featured?: boolean;
}

export interface VariantMutationPayload {
  product_id?: number;
  sku?: string;
  quantity?: number;
  unit?: string;
  mrp?: number;
  is_active?: boolean;
  net_quantity?: string | null;
  shelf_life?: string | null;
  barcode?: string | null;
  hsn_code?: string | null;
  gst_rate?: number | null;
  nutrition?: Record<string, any> | null;
}

export interface Product {
  id: number;
  name: string;
  slug: string;
  description?: string;
  search_keywords?: string | null;
  food_type?: 'veg' | 'non_veg' | 'egg' | 'not_applicable' | null;
  highlights?: ProductHighlight[] | null;
  ingredients?: string | null;
  allergen_info?: string | null;
  usage_instructions?: string | null;
  storage_instructions?: string | null;
  country_of_origin?: string | null;
  manufacturer_details?: string | null;
  marketer_details?: string | null;
  importer_details?: string | null;
  fssai_license?: string | null;
  disclaimer?: string | null;
  attributes?: Record<string, any> | null;

  categories?: {
    id: number;
    name: string;
  }[];

  isActive: boolean;
  images?: ProductImage[];
  variants?: ProductVariant[];
  brand_id?: number;
  brand?: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt?: string;
}

export interface ProductFilterState {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  categoryId?: number;
  categoryIds?: number[];
  brandId?: number;
}

export interface ProductSortState {
  field: 'created_at' | 'name';
  order: 'ASC' | 'DESC';
}

export interface GetProductsOptions {
  filters?: ProductFilterState;
  page?: number;
  pageSize?: number;
  sort?: ProductSortState;
}

export interface ProductExportResult {
  products: Product[];
  total: number;
}

export type ProductFilterOperation =
  | { method: 'ilike'; column: string; value: string }
  | { method: 'eq'; column: string; value: string | number | boolean }
  | { method: 'in'; column: string; value: number[] };

export function getProductFilterOperations(
  filters: ProductFilterState
): ProductFilterOperation[] {
  const operations: ProductFilterOperation[] = [];
  const search = filters.search?.trim();
  if (search) operations.push({ method: 'ilike', column: 'name', value: `%${search}%` });
  if (filters.status === 'active') {
    operations.push({ method: 'eq', column: 'is_active', value: true });
  }
  if (filters.status === 'inactive') {
    operations.push({ method: 'eq', column: 'is_active', value: false });
  }
  if (filters.categoryIds?.length) {
    operations.push({ method: 'in', column: 'categories.category_id', value: filters.categoryIds });
  } else if (filters.categoryId != null) {
    operations.push({ method: 'eq', column: 'categories.category_id', value: filters.categoryId });
  }
  if (filters.brandId != null) {
    operations.push({ method: 'eq', column: 'brand_id', value: filters.brandId });
  }
  return operations;
}

export interface ProductResponse {
  data: Product[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private table = 'products';
  private readonly imageAssets = inject(ImageAssetUploadService);

  /* ===================== PRODUCTS ===================== */

  private productSelect(categoryFiltered: boolean): string {
    const categoryRelation = categoryFiltered
      ? 'product_categories!inner'
      : 'product_categories';

    return `
      *,
      categories:${categoryRelation} (
        category_id,
        categories (id, name)
      ),
      images:product_images (
        id,
        is_primary,
        files!product_images_file_id_fkey (bucket, path)
      ),
      variants:product_variants (*),
      brand:brands (id, name)
    `;
  }

  private applyProductFilters(query: any, filters: ProductFilterState) {
    for (const operation of getProductFilterOperations(filters)) {
      query = query[operation.method](operation.column, operation.value);
    }
    return query;
  }

  private buildProductsQuery(
    filters: ProductFilterState,
    sort: ProductSortState,
    count: 'exact' | undefined = undefined
  ) {
    let query = supabase.from(this.table).select(
      this.productSelect(filters.categoryId != null || !!filters.categoryIds?.length),
      count ? { count } : undefined
    );
    query = this.applyProductFilters(query, filters);
    query = query.order(sort.field, { ascending: sort.order === 'ASC' });
    if (sort.field !== 'name') {
      query = query.order('name', { ascending: true });
    }
    return query.order('id', { ascending: true });
  }

  getProducts(options: GetProductsOptions = {}): Observable<ProductResponse> {
    const page = Math.max(1, options.page ?? 1);
    const limit = Math.max(1, options.pageSize ?? 10);
    const filters = { status: 'active' as const, ...options.filters };
    const sort = options.sort ?? { field: 'created_at', order: 'DESC' };
    const fromIndex = (page - 1) * limit;
    const toIndex = fromIndex + limit - 1;
    const query = this.buildProductsQuery(filters, sort, 'exact').range(
      fromIndex,
      toIndex
    );

    return from(
      query.then(({ data, count, error }) => {
        if (error) throw error;
        return {
          data: (data ?? []).map((row: any) => this.mapProduct(row)),
          meta: {
            page,
            limit,
            total: count ?? 0,
            totalPages: Math.ceil((count ?? 0) / limit),
          },
        };
      })
    );
  }

  exportProducts(
    filters: ProductFilterState,
    sort: ProductSortState,
    onProgress?: (loaded: number, total: number) => void,
    batchSize = 500
  ): Observable<ProductExportResult> {
    const safeBatchSize = Math.max(1, Math.min(batchSize, 1000));

    return from(
      (async () => {
        const products: Product[] = [];
        let total = 0;
        let offset = 0;

        do {
          const query = this.buildProductsQuery(
            filters,
            sort,
            offset === 0 ? 'exact' : undefined
          ).range(offset, offset + safeBatchSize - 1);
          const { data, count, error } = await query;
          if (error) throw error;

          if (offset === 0) total = count ?? 0;
          const batch = (data ?? []).map((row: any) => this.mapProduct(row));
          products.push(...batch);
          offset += batch.length;
          onProgress?.(products.length, total);

          if (batch.length === 0) break;
        } while (products.length < total);

        return { products, total };
      })()
    );
  }

  getAllProducts(
    page = 1,
    limit = 10,
    search = '',
    sortBy = 'created_at',
    order: 'ASC' | 'DESC' = 'DESC',
    isActive: boolean | undefined = true
  ): Observable<ProductResponse> {
    const safeSortBy = sortBy === 'name' ? 'name' : 'created_at';
    return this.getProducts({
      page,
      pageSize: limit,
      filters: {
        search,
        status:
          typeof isActive === 'undefined'
            ? 'all'
            : isActive
              ? 'active'
              : 'inactive',
      },
      sort: { field: safeSortBy, order },
    });
  }
  getProductCategoryIds(productId: number): Observable<number[]> {
    return from(
      supabase
        .from('product_categories')
        .select('category_id')
        .eq('product_id', productId)
        .then(({ data, error }) => {
          if (error) throw error;
          return (data ?? []).map((row: any) => row.category_id);
        })
    );
  }
  addProductCategories(productId: number, categoryIds: number[]) {
    return from(
      supabase.from('product_categories').insert(
        categoryIds.map((id) => ({
          product_id: productId,
          category_id: id,
        }))
      ).then(({ data, error }) => { if (error) throw error; return data; })
    );
  }

  deleteProductCategories(productId: number, categoryIds: number[]) {
    return from(
      supabase
        .from('product_categories')
        .delete()
        .eq('product_id', productId)
        .in('category_id', categoryIds)
        .then(({ data, error }) => { if (error) throw error; return data; })
    );
  }
  deleteProductVariant(variantId: number) {
    return from(
      supabase.from('product_variants').delete().eq('id', variantId).select('id')
        .then(({ data, error }) => {
          if (error) {
            if (error.code === '23503' || error.message.includes('INVENTORY_MAPPING_HAS_ORDER_HISTORY') ||
                error.message.includes('Set stock to zero')) {
              throw new Error(`Variant ${variantId} cannot be deleted because it has stock or linked order/inventory records. Turn off its Active toggle to stop sales. (${error.message})`);
            }
            throw error;
          }
          if (!data?.some(row => row.id === variantId)) {
            throw new Error(`Variant ${variantId} was not deleted. Refresh the product and check your delete permissions.`);
          }
          return data;
        })
    );
  }
  public mapProduct(row: any): Product {
    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      description: row.description ?? undefined,
      search_keywords: row.search_keywords ?? null,
      food_type: row.food_type ?? null,
      highlights: row.highlights ?? [],
      ingredients: row.ingredients ?? null,
      allergen_info: row.allergen_info ?? null,
      usage_instructions: row.usage_instructions ?? null,
      storage_instructions: row.storage_instructions ?? null,
      country_of_origin: row.country_of_origin ?? null,
      manufacturer_details: row.manufacturer_details ?? null,
      marketer_details: row.marketer_details ?? null,
      importer_details: row.importer_details ?? null,
      fssai_license: row.fssai_license ?? null,
      disclaimer: row.disclaimer ?? null,
      attributes: row.attributes ?? {},

      categories: (row.categories ?? [])
        .filter((pc: any) => pc.categories) // ⬅️ IMPORTANT
        .map((pc: any) => ({
          id: pc.categories.id,
          name: pc.categories.name,
        })),

      variants: (row.variants ?? []).map((v: any) => ({
        ...v,
        net_quantity: v.net_quantity ?? null,
        shelf_life: v.shelf_life ?? null,
        barcode: v.barcode ?? null,
        hsn_code: v.hsn_code ?? null,
        gst_rate: v.gst_rate ?? null,
        nutrition: v.nutrition ?? null
      })),
      brand: row.brand
        ? {
            id: row.brand.id,
            name: row.brand.name,
          }
        : undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      isActive: row.is_active,
      brand_id: row.brand_id,

      images: (row.images ?? []).map((img: any) => ({
        id: img.id,
        isPrimary: img.is_primary,
        url:
          img.files?.bucket && img.files?.path
            ? getPublicUrl(img.files.bucket, img.files.path)
            : null,
      })),
    };
  }

  toggleProductStatus(productId: number, currentStatus: boolean) {
    return from(
      supabase
        .from(this.table)
        .update({ is_active: !currentStatus })
        .eq('id', productId)
        .select()
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data;
        })
    );
  }

  getProductById(productId: number): Observable<Product> {
    return from(
      supabase
        .from('products')
        .select(
          `
        *,
        categories:product_categories (
          categories (
            id,
            name
          )
        ),
        images:product_images (
          id,
          is_primary,
          files!product_images_file_id_fkey (
            id,
            bucket,
            path
          )
        ),
        variants:product_variants (*),
        brand:brands (id, name)
      `
        )
        .eq('id', productId)
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapProduct(data);
        })
    );
  }

  private buildProductMutationPayload(payload: any): ProductMutationPayload {
    const nullIfEmpty = (val: any) => {
      if (typeof val === 'string') {
        const trimmed = val.trim();
        return trimmed === '' ? null : trimmed;
      }
      return val === undefined ? undefined : val;
    };

    return {
      name: payload.name,
      ...(typeof payload.description !== 'undefined' && { description: nullIfEmpty(payload.description) }),
      ...(typeof payload.search_keywords !== 'undefined' && { search_keywords: nullIfEmpty(payload.search_keywords) }),
      ...(typeof payload.isActive !== 'undefined' && { is_active: payload.isActive }),
      ...('is_active' in payload && { is_active: payload.is_active }),
      ...(typeof payload.brand_id !== 'undefined' && { brand_id: payload.brand_id }),
      
      ...(typeof payload.food_type !== 'undefined' && { food_type: nullIfEmpty(payload.food_type) }),
      ...(typeof payload.highlights !== 'undefined' && { highlights: payload.highlights }),
      ...(typeof payload.ingredients !== 'undefined' && { ingredients: nullIfEmpty(payload.ingredients) }),
      ...(typeof payload.allergen_info !== 'undefined' && { allergen_info: nullIfEmpty(payload.allergen_info) }),
      ...(typeof payload.usage_instructions !== 'undefined' && { usage_instructions: nullIfEmpty(payload.usage_instructions) }),
      ...(typeof payload.storage_instructions !== 'undefined' && { storage_instructions: nullIfEmpty(payload.storage_instructions) }),
      ...(typeof payload.country_of_origin !== 'undefined' && { country_of_origin: nullIfEmpty(payload.country_of_origin) }),
      ...(typeof payload.manufacturer_details !== 'undefined' && { manufacturer_details: nullIfEmpty(payload.manufacturer_details) }),
      ...(typeof payload.marketer_details !== 'undefined' && { marketer_details: nullIfEmpty(payload.marketer_details) }),
      ...(typeof payload.importer_details !== 'undefined' && { importer_details: nullIfEmpty(payload.importer_details) }),
      ...(typeof payload.fssai_license !== 'undefined' && { fssai_license: nullIfEmpty(payload.fssai_license) }),
      ...(typeof payload.disclaimer !== 'undefined' && { disclaimer: nullIfEmpty(payload.disclaimer) }),
      ...(typeof payload.attributes !== 'undefined' && { attributes: payload.attributes }),
    };
  }

  private buildVariantMutationPayload(payload: any): VariantMutationPayload {
    const nullIfEmpty = (val: any) => {
      if (typeof val === 'string') {
        const trimmed = val.trim();
        return trimmed === '' ? null : trimmed;
      }
      return val === undefined ? undefined : val;
    };

    let nutrition = payload.nutrition;
    if (nutrition && typeof nutrition === 'object') {
       const hasValues = Object.values(nutrition).some(val => val !== null && val !== '');
       if (!hasValues) {
           nutrition = null;
       }
    }

    return {
      ...(typeof payload.product_id !== 'undefined' && { product_id: payload.product_id }),
      sku: payload.sku,
      ...(typeof payload.quantity !== 'undefined' && { quantity: payload.quantity }),
      unit: payload.unit,
      ...(typeof payload.mrp !== 'undefined' && { mrp: payload.mrp }),
      ...(typeof payload.is_active !== 'undefined' && { is_active: payload.is_active }),
      
      ...(typeof payload.net_quantity !== 'undefined' && { net_quantity: nullIfEmpty(payload.net_quantity) }),
      ...(typeof payload.shelf_life !== 'undefined' && { shelf_life: nullIfEmpty(payload.shelf_life) }),
      ...(typeof payload.barcode !== 'undefined' && { barcode: nullIfEmpty(payload.barcode) }),
      ...(typeof payload.hsn_code !== 'undefined' && { hsn_code: nullIfEmpty(payload.hsn_code) }),
      ...(typeof payload.gst_rate !== 'undefined' && { gst_rate: nullIfEmpty(payload.gst_rate) }),
      ...(typeof payload.nutrition !== 'undefined' && { nutrition }),
    };
  }

  findProductCreation(requestId: string): Observable<Product | null> {
    return from(supabase.from(this.table).select('*').eq('creation_request_id', requestId)
      .maybeSingle().then(({ data, error }) => { if (error) throw error; return data; }));
  }

  createProductBundle(requestId: string, payload: any, categoryIds: number[], variants: any[]): Observable<Product> {
    return from(supabase.rpc('admin_create_product_bundle', {
      p_request_id: requestId, p_product: this.buildProductMutationPayload(payload),
      p_category_ids: categoryIds, p_variants: variants.map(v => this.buildVariantMutationPayload(v)),
    }).then(({ data, error }) => { if (error) throw error; return data; }));
  }

  createProduct(payload: any): Observable<Product> {
    console.log(payload, 'payloads');
    const dbPayload = this.buildProductMutationPayload(payload);
    return from(
      supabase
        .from(this.table)
        .insert(dbPayload)
        .select()
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data;
        })
    );
  }

  updateProduct(productId: number, payload: Partial<Product>) {
    return from(
      supabase
        .from(this.table)
        .update(payload)
        .eq('id', productId)
        .select()
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data;
        })
    );
  }
  saveProductCatalog(productId: number, payload: any, categoryIds: number[], expectedCategoryIds: number[]): Observable<Product> {
    return from(supabase.rpc('admin_save_product_catalog', {
      p_product_id: productId, p_product: this.buildProductMutationPayload(payload),
      p_category_ids: categoryIds, p_expected_category_ids: expectedCategoryIds,
    }).then(({ data, error }) => { if (error) throw error; return this.mapProduct(data); }));
  }

  deleteProduct(productId: number) {
    return from(
      supabase
        .from(this.table)
        .delete()
        .eq('id', productId)
        .then(({ error }) => {
          if (error) throw error;
          return true;
        })
    );
  }

  /* ===================== PRODUCT IMAGES ===================== */

  uploadProductImage(
    productId: number,
    file: File,
    isPrimary = false,
    requestId: string = crypto.randomUUID()
  ): Observable<string> {
    return from(
      (async () => {
        /* 0️⃣ Compress image */
        const asset = await this.imageAssets.upload({
          file,
          bucket: 'main-bucket',
          basePath: `products/${productId}`,
          kind: 'product',
          entityType: 'product',
          entityId: productId,
          purpose: 'image',
          isPrimary,
          requestId,
        });

        /* 1️⃣ Upload to storage */


        /* 2️⃣ Insert into files table (SET file_name + size) */


        const { error: imageError } = await supabase.rpc('admin_attach_product_image', {
          p_request_id: requestId, p_product_id: productId,
          p_file_id: asset.fileId, p_is_primary: isPrimary,
        });

        if (imageError) throw imageError;

        /* 4️⃣ Return public URL (computed) */
        return asset.masterUrl;
      })()
    );
  }

  getProductImages(productId: number) {
    return from(
      supabase
        .from('product_images')
        .select(
          `
        id,
        is_primary,
        sort_order,
        upload_request_id,
        files:files!product_images_file_id_fkey (
          bucket,
          path
        )
      `
        )
        .eq('product_id', productId)
        .then(({ data, error }) => {
          if (error) throw error;

          return (data ?? []).map((row: any) => {
            const { data: urlData } = supabase.storage
              .from(row.files.bucket)
              .getPublicUrl(row.files.path);

            return {
              id: row.id,
              uploadRequestId: row.upload_request_id,
              isPrimary: row.is_primary,
              url: urlData.publicUrl,
            };
          });
        })
    );
  }

  setPrimaryImage(imageId: number, productId: number): Observable<boolean> {
    return from(
      (async () => {
        if (!imageId || !productId) {
          throw new Error('Product image id and product id are required');
        }

        // 1️⃣ Check image belongs to this product
        const { data: images, error: imageError } = await supabase
          .from('product_images')
          .select('id, product_id')
          .eq('id', imageId)
          .eq('product_id', productId);

        if (imageError) throw imageError;

        if (!images?.length) {
          throw new Error(
            `Image not found for imageId=${imageId}, productId=${productId}`
          );
        }

        // 2️⃣ Make all images false
        const { error: resetError } = await supabase
          .from('product_images')
          .update({ is_primary: false })
          .eq('product_id', productId);

        if (resetError) throw resetError;

        // 3️⃣ Make selected image true
        const { data: updatedImages, error: primaryError } = await supabase
          .from('product_images')
          .update({ is_primary: true })
          .eq('id', imageId)
          .eq('product_id', productId)
          .select('id, product_id, is_primary');

        if (primaryError) throw primaryError;

        const updatedImage = updatedImages?.[0];

        if (!updatedImage?.is_primary) {
          throw new Error(
            `Failed to set primary image for imageId=${imageId}, productId=${productId}`
          );
        }

        return true;
      })()
    );
  }

  deleteProductImage(imageId: number): Observable<boolean> {
    return from(
      (async () => {
        /* 1️⃣ Get file_id from product_images */
        const { data: image, error: imageError } = await supabase
          .from('product_images')
          .select('id, file_id')
          .eq('id', imageId)
          .maybeSingle();

        if (imageError) throw imageError;
        if (!image?.file_id) {
          throw new Error('Product image not found');
        }

        const fileId = image.file_id;

        /* 2️⃣ Get file info from files table */
        const { data: file, error: fileError } = await supabase
          .from('files')
          .select('id, bucket, path')
          .eq('id', fileId)
          .maybeSingle();

        if (fileError) throw fileError;
        if (!file?.bucket || !file?.path) {
          throw new Error('File reference not found');
        }

        /* 3️⃣ Delete from product_images FIRST (as you want) */
        const { error: piDeleteError } = await supabase
          .from('product_images')
          .delete()
          .eq('id', imageId);

        if (piDeleteError) throw piDeleteError;

        /* 4️⃣ Delete from files table */
        const { error: filesDeleteError } = await supabase
          .from('files')
          .delete()
          .eq('id', fileId);

        if (filesDeleteError) throw filesDeleteError;

        /* 5️⃣ Delete from storage bucket */
        const { error: storageError } = await supabase.storage
          .from(file.bucket)
          .remove([file.path]);

        if (storageError) throw storageError;

        return true;
      })()
    );
  }
  getProductVariants(productId: number): Observable<ProductVariant[]> {
    return from(
      supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .then(({ data, error }) => {
          if (error) throw error;
          return data ?? [];
        })
    );
  }
  /* ===================== VARIANTS ===================== */

  addProductVariant(productId: number, payload: any) {
    const dbPayload = this.buildVariantMutationPayload({
      ...payload,
      product_id: productId
    });
    return from(
      supabase
        .from('product_variants')
        .insert(dbPayload)
        .select()
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data;
        })
    );
  }
  updateProductDetails(productId: number, payload: any): Observable<Product> {
    const dbPayload = this.buildProductMutationPayload(payload);
    return from(
      supabase
        .from(this.table)
        .update(dbPayload)
        .eq('id', productId)
        .select()
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapProduct(data);
        })
    );
  }
  updateProductSlug(productId: number, slug: string): Observable<boolean> {
    return from(
      supabase
        .from(this.table)
        .update({ slug })
        .eq('id', productId)
        .then(({ error }) => {
          if (error) throw error;
          return true;
        })
    );
  }
  updateProductVariant(variantId: number, payload: any) {
    const dbPayload = this.buildVariantMutationPayload(payload);
    delete dbPayload.product_id; // don't update product_id in update
    return from(
      supabase
        .from('product_variants')
        .update(dbPayload)
        .eq('id', variantId)
        .select()
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data;
        })
    );
  }
  toggleVariantStatus(variantId: number, isActive: boolean) {
    return from(
      supabase
        .from('product_variants')
        .update({ is_active: !isActive })
        .eq('id', variantId)
        .then(({ error }) => {
          if (error) throw error;
          return true;
        })
    );
  }
  searchOrLatestProducts(search = '', limit = 2): Observable<Product[]> {
    let query = supabase
      .from(this.table)
      .select(
        `
    *,
    categories:product_categories (
      categories (
        id,
        name
      )
    ),
    images:product_images (
      id,
      is_primary,
      files!product_images_file_id_fkey (
        id,
        bucket,
        path
      )
    ),
    variants:product_variants (*)
  `
      )
      .eq('is_active', true);

    // 🔍 If user is searching
    if (search && search.trim().length > 0) {
      query = query
        .ilike('name', `%${search.trim()}%`)
        .order('name', { ascending: true })
        .limit(10); // more results for search
    }
    // 🆕 Initial load → latest 2
    else {
      query = query.order('created_at', { ascending: false }).limit(limit);
    }

    return from(
      query.then(({ data, error }) => {
        if (error) throw error;
        return (data ?? []).map((row: any) => this.mapProduct({ ...row, variants: (row.variants ?? []).filter((variant: ProductVariant) => variant.is_active === true) }));
      })
    );
  }

  getProductsForShelfPreview(filters: {
    categoryIds?: number[];
    brandId?: number;
    sortBy?: string;
    limit?: number;
  }): Observable<Product[]> {
    const categoryIds = (filters.categoryIds || []).filter(Number.isFinite);
    const categoryJoin = categoryIds.length
      ? 'product_categories!inner'
      : 'product_categories';
    const limit = Math.min(50, Math.max(1, Number(filters.limit) || 10));

    let query = supabase
      .from(this.table)
      .select(
        `
        *,
        categories:${categoryJoin} (
          category_id,
          categories (id, name)
        ),
        brand:brands (id, name),
        images:product_images (
          id,
          is_primary,
          files!product_images_file_id_fkey (bucket, path)
        ),
        variants:product_variants (*)
        `
      )
      .eq('is_active', true)
      .order(filters.sortBy === 'featured' ? 'id' : 'created_at', {
        ascending: false,
      })
      .limit(limit);

    if (categoryIds.length) {
      query = query.in('product_categories.category_id', categoryIds);
    }
    if (filters.brandId) {
      query = query.eq('brand_id', filters.brandId);
    }

    return from(
      query.then(({ data, error }) => {
        if (error) throw error;
        const products = (data ?? []).map((row: any) => this.mapProduct(row));
        const getPrice = (product: Product) =>
          Number(product.variants?.find(variant => variant.is_active !== false)?.price ?? Number.MAX_SAFE_INTEGER);

        if (filters.sortBy === 'price_asc') {
          products.sort((a, b) => getPrice(a) - getPrice(b));
        } else if (filters.sortBy === 'price_desc') {
          products.sort((a, b) => getPrice(b) - getPrice(a));
        }
        return products;
      })
    );
  }

  getProductsByFilters(filters: any): Observable<Product[]> {
    let query = supabase
      .from(this.table)
      .select(
        `
        *,
        categories:product_categories!inner (
          category_id,
          categories (id, name)
        ),
        brand:brands (id, name),
        images:product_images (
          is_primary,
          files!product_images_file_id_fkey (bucket, path)
        ),
        variants:product_variants (*)
        `
      )
      .eq('is_active', true);

    if (filters.selectedCategory) {
      query = query.eq(
        'product_categories.category_id',
        filters.selectedCategory
      );
    }

    if (filters.brands && filters.brands.length > 0) {
      query = query.in('brand.name', filters.brands);
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    const sortBy = filters.sortBy || 'newest';
    if (sortBy === 'newest') {
      query = query.order('created_at', { ascending: false });
    } else if (sortBy === 'featured') {
      query = query.order('id', { ascending: false });
    }

    return from(
      query.then(({ data, error }) => {
        if (error) throw error;
        return (data ?? []).map((row: any) => this.mapProduct(row));
      })
    );
  }
}
